import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../components/header/Header';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import {
    Box,
    Autocomplete,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Chip,
    Card,
    CardContent,
    Button,
    TextField,
} from '@mui/material';
import {
    People,
    Person,
    Badge,
    Business,
    Security,
    SmartToy,
    Hub,
    Edit,
    Save,
    Close,
} from '@mui/icons-material';
import { useAuth } from '../authentication/AuthContext';
import axiosInstance from '../authentication/AuthContext';

function getConfigurationAsObject(configuration) {
    if (
        configuration &&
        typeof configuration === 'object' &&
        !Array.isArray(configuration)
    ) {
        return { ...configuration };
    }
    return {};
}

function isApiKeyField(key) {
    const normalizedKey = String(key || '').toLowerCase();
    return (
        normalizedKey.includes('api_key') || normalizedKey.includes('apikey')
    );
}

function normalizeLlmConfiguration(configuration) {
    const obj = getConfigurationAsObject(configuration);
    const apiKeyEntry = Object.entries(obj).find(([key]) => isApiKeyField(key));
    const apiKeyFromAlias =
        apiKeyEntry?.[1] != null ? String(apiKeyEntry[1]) : '';
    return {
        model:
            obj.model != null && String(obj.model).trim() !== ''
                ? String(obj.model)
                : '',
        api_key:
            obj.api_key != null && String(obj.api_key).trim() !== ''
                ? String(obj.api_key)
                : apiKeyFromAlias,
    };
}

function normalizeModelConfiguration(configuration) {
    const obj = getConfigurationAsObject(configuration);
    const apiKeyEntry = Object.entries(obj).find(([key]) => isApiKeyField(key));
    const apiKeyFromAlias =
        apiKeyEntry?.[1] != null ? String(apiKeyEntry[1]) : '';
    return {
        provider:
            obj.provider != null && String(obj.provider).trim() !== ''
                ? String(obj.provider)
                : '',
        model:
            obj.model != null && String(obj.model).trim() !== ''
                ? String(obj.model)
                : '',
        api_key:
            obj.api_key != null && String(obj.api_key).trim() !== ''
                ? String(obj.api_key)
                : apiKeyFromAlias,
    };
}

function normalizeDepartments(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.departments)) return data.departments;
    return [];
}

function getDepartmentName(department) {
    if (typeof department === 'string') return department;
    if (!department || typeof department !== 'object') return '';
    return (
        department.name ||
        department.department_name ||
        department.department ||
        ''
    );
}

function getDepartmentKey(department) {
    if (department == null) return '';
    if (typeof department === 'string') return department;
    return (
        department.id ??
        department.uuid ??
        department.department_id ??
        getDepartmentName(department)
    );
}

function getUserId(user) {
    if (!user || typeof user !== 'object') return null;
    return user.id ?? user.pk ?? user.user_id ?? null;
}

function normalizeUserDepartments(user) {
    if (!user) return [];
    if (Array.isArray(user.departments)) return user.departments;
    if (Array.isArray(user.department)) return user.department;
    if (typeof user.department === 'string' && user.department.trim()) {
        return [user.department.trim()];
    }
    return [];
}

const MODEL_CONFIGURATION_TYPES = ['llm', 'reranker', 'embedder'];

function normalizeModelsByProvider(modelsByProvider) {
    if (!modelsByProvider || typeof modelsByProvider !== 'object') {
        return {};
    }
    return Object.fromEntries(
        Object.entries(modelsByProvider).map(([provider, value]) => [
            provider,
            Array.isArray(value) ? value : (value?.models ?? []),
        ])
    );
}

function createInitialModelConfigurationOptions() {
    return {
        llm: { models: [] },
        reranker: { providers: [], modelsByProvider: {} },
        embedder: { providers: [], modelsByProvider: {} },
    };
}

export default function UserManagement() {
    const { accessToken, user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingConfigurationType, setEditingConfigurationType] =
        useState(null);
    const [configurationDrafts, setConfigurationDrafts] = useState({
        llm: {},
        reranker: {},
        embedder: {},
    });
    const [availableDepartments, setAvailableDepartments] = useState([]);
    const [departmentDraft, setDepartmentDraft] = useState([]);
    const [savingDepartments, setSavingDepartments] = useState(false);
    const [modelConfigurationOptions, setModelConfigurationOptions] = useState(
        createInitialModelConfigurationOptions
    );

    const getUserDepartments = useCallback(async (selectedUser) => {
        if (selectedUser == null) {
            throw new Error('Selected user is null');
        }
        const response = await axiosInstance.get(
            `/accounts/users/${selectedUser.username}/departments/`
        );
        return normalizeDepartments(response.data);
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axiosInstance.get(
                    '/accounts/get-users/'
                );
                let usersData = response.data;
                if (Array.isArray(response.data)) {
                    usersData = response.data;
                } else if (response.data?.results) {
                    usersData = response.data.results;
                } else if (response.data?.users) {
                    usersData = response.data.users;
                } else {
                    usersData = response.data ?? [];
                }
                const normalizedUsers = Array.isArray(usersData)
                    ? usersData
                    : [];
                const usersWithDepartments = await Promise.all(
                    normalizedUsers.map(async (user) => {
                        const userId = getUserId(user);
                        if (userId == null) return user;
                        try {
                            const userDepartments =
                                await getUserDepartments(userId);
                            return {
                                ...user,
                                departments: userDepartments,
                            };
                        } catch (departmentErr) {
                            console.error(
                                `Failed to load departments for user ${userId}:`,
                                departmentErr
                            );
                            return user;
                        }
                    })
                );
                setUsers(usersWithDepartments);
                if (usersWithDepartments.length > 0) {
                    setSelectedUser(usersWithDepartments[0]);
                }
            } catch (err) {
                console.error('Failed to fetch users:', err);
                setError(err.response?.data?.detail || 'Failed to load users');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            fetchUsers();
        }
    }, [accessToken, getUserDepartments]);

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axiosInstance.get(
                    '/accounts/departments/'
                );
                setAvailableDepartments(normalizeDepartments(response.data));
            } catch (err) {
                console.error('Failed to fetch departments:', err);
            }
        };

        if (accessToken) {
            fetchDepartments();
        }
    }, [accessToken]);

    useEffect(() => {
        const fetchModelConfigurationOptions = async () => {
            try {
                const results = await Promise.all(
                    MODEL_CONFIGURATION_TYPES.map(async (type) => {
                        const response = await axiosInstance.get(
                            `/accounts/model-configuration/${type}/`
                        );
                        return { type, data: response.data };
                    })
                );

                setModelConfigurationOptions((prev) => {
                    const next = { ...prev };
                    for (const { type, data } of results) {
                        if (type === 'llm') {
                            next.llm = {
                                models: data?.models ?? [],
                            };
                        } else {
                            next[type] = {
                                providers: data?.providers ?? [],
                                modelsByProvider: normalizeModelsByProvider(
                                    data?.models_by_provider
                                ),
                            };
                        }
                    }
                    return next;
                });
            } catch (err) {
                console.error(
                    'Failed to fetch model configuration options:',
                    err
                );
                setError(
                    err.response?.data?.detail ||
                        err.response?.data?.error ||
                        'Failed to load model configuration options'
                );
            }
        };

        if (accessToken) {
            fetchModelConfigurationOptions();
        }
    }, [accessToken]);

    const handleUserSelect = (user) => {
        setSelectedUser(user);
    };

    const getUserDisplayName = (user) => {
        if (!user) return 'Unknown user';
        const fullName =
            `${user.first_name || ''} ${user.last_name || ''}`.trim();
        return fullName || user.username || 'Unknown user';
    };

    const getUserKey = (user, index) => {
        if (!user) return `unknown-${index}`;
        if (user.id != null) return `id-${user.id}`;
        if (user.username) return `username-${user.username}`;
        return `fallback-${index}-${user.first_name || ''}-${user.last_name || ''}`;
    };

    useEffect(() => {
        if (!selectedUser) return;
        let isCancelled = false;

        const loadSelectedUserDepartments = async () => {
            setEditingConfigurationType(null);
            setConfigurationDrafts({
                llm: normalizeLlmConfiguration(
                    selectedUser.user_llm_configuration
                ),
                reranker: normalizeModelConfiguration(
                    selectedUser.user_reranker_configuration
                ),
                embedder: normalizeModelConfiguration(
                    selectedUser.user_embedder_configuration
                ),
            });

            try {
                const userDepartments = await getUserDepartments(selectedUser);
                if (!isCancelled) {
                    setDepartmentDraft(userDepartments);
                }
            } catch (err) {
                console.error(
                    `Failed to load departments for user ${selectedUser}:`,
                    err
                );
                if (!isCancelled) {
                    setDepartmentDraft(normalizeUserDepartments(selectedUser));
                }
            }
        };

        loadSelectedUserDepartments();

        return () => {
            isCancelled = true;
        };
    }, [selectedUser, getUserDepartments]);

    const isSameUser = (userA, userB) => {
        if (!userA || !userB) return false;
        if (userA.id != null && userB.id != null) return userA.id === userB.id;
        if (userA.username && userB.username)
            return userA.username === userB.username;
        return false;
    };

    const isLoggedInUser = useCallback(
        (listUser) => {
            if (!currentUser || !listUser) return false;
            const selfId = currentUser.id ?? currentUser.pk;
            const rowId = listUser.id ?? listUser.pk;
            if (selfId != null && rowId != null) {
                return Number(selfId) === Number(rowId);
            }
            if (currentUser.username && listUser.username) {
                return currentUser.username === listUser.username;
            }
            return false;
        },
        [currentUser]
    );

    const displayUsers = useMemo(() => {
        if (!users.length) return [];
        if (!currentUser) return users;
        const current = users.filter((u) => isLoggedInUser(u));
        const rest = users.filter((u) => !isLoggedInUser(u));
        return [...current, ...rest];
    }, [users, currentUser, isLoggedInUser]);

    const handleEditConfiguration = (type) => {
        setEditingConfigurationType(type);
    };

    const handleCancelConfigurationEdit = () => {
        if (!selectedUser) return;
        setConfigurationDrafts({
            llm: normalizeLlmConfiguration(selectedUser.user_llm_configuration),
            reranker: normalizeModelConfiguration(
                selectedUser.user_reranker_configuration
            ),
            embedder: normalizeModelConfiguration(
                selectedUser.user_embedder_configuration
            ),
        });
        setEditingConfigurationType(null);
    };

    const handleConfigurationFieldChange = (type, key, value) => {
        setConfigurationDrafts((prev) => ({
            ...prev,
            [type]: {
                ...(prev[type] || {}),
                [key]: value,
            },
        }));
    };

    const handleProviderChange = (type, providerId) => {
        setConfigurationDrafts((prev) => ({
            ...prev,
            [type]: {
                ...(prev[type] || {}),
                provider: providerId,
                model: '',
            },
        }));
    };

    const getProviderLabel = (type, providerId) => {
        if (!providerId) return '-';
        const providers = modelConfigurationOptions[type]?.providers ?? [];
        const match = providers.find((provider) => provider.id === providerId);
        return match?.label || providerId;
    };

    const handleSaveConfiguration = async (selectedUser, type) => {
        if (selectedUser == null) {
            setError('Unable to determine the selected user id');
            return;
        }

        const fieldNameByType = {
            llm: 'user_llm_configuration',
            reranker: 'user_reranker_configuration',
            embedder: 'user_embedder_configuration',
        };
        const fieldName = fieldNameByType[type];
        if (!fieldName) return;

        const updatedConfiguration =
            type === 'llm'
                ? normalizeLlmConfiguration(configurationDrafts[type] || {})
                : normalizeModelConfiguration(
                      configurationDrafts[type] || {}
                  );

        const payload = {
            user_id: selectedUser.username,
            configuration_type: type,
            configuration: updatedConfiguration,
        };

        try {
            setError(null);
            await axiosInstance.post(
                '/accounts/user-model-configuration/',
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const updatedSelectedUser = {
                ...selectedUser,
                [fieldName]: updatedConfiguration,
            };
            setSelectedUser(updatedSelectedUser);
            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    isSameUser(user, selectedUser) ? updatedSelectedUser : user
                )
            );
            setEditingConfigurationType(null);
        } catch (err) {
            console.error('Failed to save model configuration:', err);
            setError(
                err.response?.data?.detail ||
                    err.response?.data?.message ||
                    'Failed to save model configuration'
            );
        }
    };

    const hasApiKeyInConfiguration = (configuration) => {
        if (
            !configuration ||
            typeof configuration !== 'object' ||
            Array.isArray(configuration)
        ) {
            return false;
        }

        const apiKeyEntry = Object.entries(configuration).find(
            ([key, value]) => {
                const normalizedKey = String(key).toLowerCase();
                const looksLikeApiKeyField = isApiKeyField(normalizedKey);

                if (!looksLikeApiKeyField) {
                    return false;
                }

                if (typeof value === 'string') {
                    return value.trim().length > 0;
                }

                return value != null && String(value).trim().length > 0;
            }
        );

        return Boolean(apiKeyEntry);
    };

    const renderLlmConfigurationView = (configuration) => {
        const normalized = normalizeLlmConfiguration(configuration);
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                    <strong>Model:</strong>{' '}
                    {normalized.model.trim() ? normalized.model : '-'}
                </Typography>
                <Typography variant="body2">
                    <strong>API key:</strong>{' '}
                    {normalized.api_key.trim() ? '********' : '-'}
                </Typography>
            </Box>
        );
    };

    const renderLlmConfigurationEditor = () => {
        const draft = normalizeLlmConfiguration(configurationDrafts.llm || {});
        const availableModels = modelConfigurationOptions.llm?.models ?? [];
        const modelOptions =
            draft.model && !availableModels.includes(draft.model)
                ? [...availableModels, draft.model]
                : availableModels;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Autocomplete
                    options={modelOptions}
                    value={draft.model || null}
                    onChange={(_event, newValue) =>
                        handleConfigurationFieldChange(
                            'llm',
                            'model',
                            newValue ?? ''
                        )
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Model"
                            size="small"
                            margin="dense"
                        />
                    )}
                    fullWidth
                />
                <TextField
                    label="API key"
                    value={draft.api_key}
                    onChange={(event) =>
                        handleConfigurationFieldChange(
                            'llm',
                            'api_key',
                            event.target.value
                        )
                    }
                    fullWidth
                    size="small"
                    margin="dense"
                    type="password"
                    autoComplete="new-password"
                />
            </Box>
        );
    };

    const renderModelConfigurationView = (configuration, type) => {
        const normalized = normalizeModelConfiguration(configuration);
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                    <strong>Provider:</strong>{' '}
                    {normalized.provider.trim()
                        ? getProviderLabel(type, normalized.provider)
                        : '-'}
                </Typography>
                <Typography variant="body2">
                    <strong>Model:</strong>{' '}
                    {normalized.model.trim() ? normalized.model : '-'}
                </Typography>
                <Typography variant="body2">
                    <strong>API key:</strong>{' '}
                    {normalized.api_key.trim() ? '********' : '-'}
                </Typography>
            </Box>
        );
    };

    const renderModelConfigurationEditor = (type) => {
        const draft = normalizeModelConfiguration(
            configurationDrafts[type] || {}
        );
        const providers = modelConfigurationOptions[type]?.providers ?? [];
        const providerOptions =
            draft.provider &&
            !providers.some((provider) => provider.id === draft.provider)
                ? [...providers, { id: draft.provider, label: draft.provider }]
                : providers;
        const availableModels = draft.provider
            ? (modelConfigurationOptions[type]?.modelsByProvider?.[
                  draft.provider
              ] ?? [])
            : [];
        const modelOptions =
            draft.model && !availableModels.includes(draft.model)
                ? [...availableModels, draft.model]
                : availableModels;
        const selectedProvider =
            providerOptions.find(
                (provider) => provider.id === draft.provider
            ) ?? null;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Autocomplete
                    options={providerOptions}
                    value={selectedProvider}
                    onChange={(_event, newValue) =>
                        handleProviderChange(type, newValue?.id ?? '')
                    }
                    getOptionLabel={(option) => option.label || option.id}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Provider"
                            size="small"
                            margin="dense"
                        />
                    )}
                    fullWidth
                />
                <Autocomplete
                    options={modelOptions}
                    value={draft.model || null}
                    onChange={(_event, newValue) =>
                        handleConfigurationFieldChange(
                            type,
                            'model',
                            newValue ?? ''
                        )
                    }
                    disabled={!draft.provider}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Model"
                            size="small"
                            margin="dense"
                        />
                    )}
                    fullWidth
                />
                <TextField
                    label="API key"
                    value={draft.api_key}
                    onChange={(event) =>
                        handleConfigurationFieldChange(
                            type,
                            'api_key',
                            event.target.value
                        )
                    }
                    fullWidth
                    size="small"
                    margin="dense"
                    type="password"
                    autoComplete="new-password"
                />
            </Box>
        );
    };

    const handleSaveDepartments = async (selectedUser) => {
        if (selectedUser == null) {
            setError('Unable to determine the selected user id');
            return;
        }

        const normalizedDepartments = departmentDraft
            .map((department) =>
                typeof department === 'string'
                    ? department.trim()
                    : getDepartmentName(department).trim()
            )
            .filter(Boolean);

        try {
            setSavingDepartments(true);
            setError(null);

            await axiosInstance.post(
                `/accounts/users/${selectedUser.username}/departments/`,
                {
                    departments: normalizedDepartments.map((name) => ({
                        name,
                    })),
                }
            );

            const updatedSelectedUser = {
                ...selectedUser,
                departments: normalizedDepartments,
                department:
                    normalizedDepartments.length === 1
                        ? normalizedDepartments[0]
                        : selectedUser.department,
            };
            setSelectedUser(updatedSelectedUser);
            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    isSameUser(user, selectedUser) ? updatedSelectedUser : user
                )
            );
        } catch (err) {
            console.error('Failed to save departments:', err);
            setError(
                err.response?.data?.detail ||
                    err.response?.data?.message ||
                    'Failed to update departments'
            );
        } finally {
            setSavingDepartments(false);
        }
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.default',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    {loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {!loading && !error && (
                        <Grid
                            container
                            spacing={3}
                            sx={{ height: 'calc(100vh - 120px)' }}
                        >
                            <Grid item size={4}>
                                <Paper
                                    sx={{
                                        height: '100%',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: 'background.paper',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <People />
                                            Users ({users.length})
                                        </Typography>
                                    </Box>

                                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                                        {users.length === 0 ? (
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    No users found
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <List sx={{ p: 0 }}>
                                                {displayUsers.map(
                                                    (user, index) => (
                                                        <React.Fragment
                                                            key={getUserKey(
                                                                user,
                                                                index
                                                            )}
                                                        >
                                                            <ListItem
                                                                disablePadding
                                                            >
                                                                <ListItemButton
                                                                    selected={
                                                                        getUserKey(
                                                                            selectedUser,
                                                                            -1
                                                                        ) ===
                                                                        getUserKey(
                                                                            user,
                                                                            index
                                                                        )
                                                                    }
                                                                    onClick={() =>
                                                                        handleUserSelect(
                                                                            user
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        '&.Mui-selected':
                                                                            {
                                                                                backgroundColor:
                                                                                    'primary.light',
                                                                                '&:hover':
                                                                                    {
                                                                                        backgroundColor:
                                                                                            'primary.light',
                                                                                    },
                                                                            },
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    gap: 1,
                                                                                    mb: 0.5,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="subtitle2"
                                                                                    noWrap
                                                                                    component="span"
                                                                                >
                                                                                    {getUserDisplayName(
                                                                                        user
                                                                                    )}
                                                                                </Typography>
                                                                                {isLoggedInUser(
                                                                                    user
                                                                                ) && (
                                                                                    <Typography
                                                                                        component="span"
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            fontStyle:
                                                                                                'italic',
                                                                                            color: 'text.secondary',
                                                                                            flexShrink: 0,
                                                                                        }}
                                                                                    >
                                                                                        (me)
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                        secondary={
                                                                            <Box
                                                                                sx={{
                                                                                    mt: 0.5,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                    display="block"
                                                                                >
                                                                                    @
                                                                                    {user.username ||
                                                                                        '-'}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </ListItemButton>
                                                            </ListItem>
                                                            {index <
                                                                displayUsers.length -
                                                                    1 && (
                                                                <Divider />
                                                            )}
                                                        </React.Fragment>
                                                    )
                                                )}
                                            </List>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item size={8}>
                                <Paper
                                    sx={{ height: '100%', overflow: 'auto' }}
                                >
                                    {selectedUser ? (
                                        <Box sx={{ p: 3 }}>
                                            <Box
                                                sx={{
                                                    mb: 3,
                                                    pb: 2,
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        mb: 1.5,
                                                    }}
                                                >
                                                    <Person
                                                        sx={{
                                                            fontSize: 40,
                                                            color: 'primary.main',
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography variant="h5">
                                                            {getUserDisplayName(
                                                                selectedUser
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            @
                                                            {selectedUser.username ||
                                                                '-'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Grid
                                                container
                                                spacing={2}
                                                columns={12}
                                            >
                                                <Grid item size={6}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Typography
                                                                variant="h6"
                                                                gutterBottom
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Badge />
                                                                Account
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    First name:
                                                                </strong>{' '}
                                                                {selectedUser.first_name ||
                                                                    '-'}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Last name:
                                                                </strong>{' '}
                                                                {selectedUser.last_name ||
                                                                    '-'}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Username:
                                                                </strong>{' '}
                                                                {selectedUser.username ||
                                                                    '-'}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Email:
                                                                </strong>{' '}
                                                                {selectedUser.email ||
                                                                    '-'}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>

                                                <Grid item size={6}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Typography
                                                                variant="h6"
                                                                gutterBottom
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Business />
                                                                Organization
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Departments:
                                                                </strong>{' '}
                                                            </Typography>
                                                            <Box
                                                                sx={{ mt: 1.5 }}
                                                            >
                                                                <Autocomplete
                                                                    multiple
                                                                    options={
                                                                        availableDepartments
                                                                    }
                                                                    value={
                                                                        departmentDraft
                                                                    }
                                                                    onChange={(
                                                                        _event,
                                                                        newValue
                                                                    ) =>
                                                                        setDepartmentDraft(
                                                                            newValue
                                                                        )
                                                                    }
                                                                    getOptionLabel={(
                                                                        option
                                                                    ) =>
                                                                        getDepartmentName(
                                                                            option
                                                                        )
                                                                    }
                                                                    isOptionEqualToValue={(
                                                                        option,
                                                                        value
                                                                    ) =>
                                                                        String(
                                                                            getDepartmentKey(
                                                                                option
                                                                            )
                                                                        ) ===
                                                                        String(
                                                                            getDepartmentKey(
                                                                                value
                                                                            )
                                                                        )
                                                                    }
                                                                    renderInput={(
                                                                        params
                                                                    ) => (
                                                                        <TextField
                                                                            {...params}
                                                                            size="small"
                                                                            label="Departments"
                                                                            placeholder="Select departments"
                                                                        />
                                                                    )}
                                                                    renderTags={(
                                                                        value,
                                                                        getTagProps
                                                                    ) =>
                                                                        value.map(
                                                                            (
                                                                                option,
                                                                                index
                                                                            ) => (
                                                                                <Chip
                                                                                    key={`department-chip-${getDepartmentKey(option) || index}`}
                                                                                    label={
                                                                                        getDepartmentName(
                                                                                            option
                                                                                        ) ||
                                                                                        'Unnamed department'
                                                                                    }
                                                                                    size="small"
                                                                                    {...getTagProps(
                                                                                        {
                                                                                            index,
                                                                                        }
                                                                                    )}
                                                                                />
                                                                            )
                                                                        )
                                                                    }
                                                                    noOptionsText="No departments available"
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        justifyContent:
                                                                            'flex-end',
                                                                        mt: 1.5,
                                                                    }}
                                                                >
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        onClick={() =>
                                                                            handleSaveDepartments(
                                                                                selectedUser
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            savingDepartments
                                                                        }
                                                                    >
                                                                        {savingDepartments
                                                                            ? 'Saving...'
                                                                            : 'Save'}
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>

                                                <Grid item size={12}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Typography
                                                                variant="h6"
                                                                gutterBottom
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <SmartToy />
                                                                Model
                                                                Configuration
                                                            </Typography>
                                                            <Grid
                                                                container
                                                                spacing={2}
                                                            >
                                                                <Grid
                                                                    item
                                                                    size={{
                                                                        xs: 12,
                                                                        md: 4,
                                                                    }}
                                                                >
                                                                    <Card variant="outlined">
                                                                        <CardContent>
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'space-between',
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: 1,
                                                                                    }}
                                                                                >
                                                                                    <SmartToy fontSize="small" />
                                                                                    LLM
                                                                                </Typography>
                                                                                {editingConfigurationType ===
                                                                                'llm' ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display:
                                                                                                'flex',
                                                                                            gap: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="contained"
                                                                                            startIcon={
                                                                                                <Save />
                                                                                            }
                                                                                            onClick={() =>
                                                                                                handleSaveConfiguration(
                                                                                                    selectedUser,
                                                                                                    'llm'
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Save
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            color="inherit"
                                                                                            startIcon={
                                                                                                <Close />
                                                                                            }
                                                                                            onClick={
                                                                                                handleCancelConfigurationEdit
                                                                                            }
                                                                                        >
                                                                                            Cancel
                                                                                        </Button>
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        startIcon={
                                                                                            <Edit />
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleEditConfiguration(
                                                                                                'llm'
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </Button>
                                                                                )}
                                                                            </Box>
                                                                            {!hasApiKeyInConfiguration(
                                                                                selectedUser.user_llm_configuration
                                                                            ) && (
                                                                                <Alert
                                                                                    severity="warning"
                                                                                    sx={{
                                                                                        mb: 1.5,
                                                                                    }}
                                                                                >
                                                                                    Missing
                                                                                    LLM
                                                                                    API
                                                                                    key.
                                                                                </Alert>
                                                                            )}
                                                                            {editingConfigurationType ===
                                                                            'llm'
                                                                                ? renderLlmConfigurationEditor()
                                                                                : renderLlmConfigurationView(
                                                                                      selectedUser.user_llm_configuration
                                                                                  )}
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    size={{
                                                                        xs: 12,
                                                                        md: 4,
                                                                    }}
                                                                >
                                                                    <Card variant="outlined">
                                                                        <CardContent>
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'space-between',
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: 1,
                                                                                    }}
                                                                                >
                                                                                    <Security fontSize="small" />
                                                                                    Reranker
                                                                                </Typography>
                                                                                {editingConfigurationType ===
                                                                                'reranker' ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display:
                                                                                                'flex',
                                                                                            gap: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="contained"
                                                                                            startIcon={
                                                                                                <Save />
                                                                                            }
                                                                                            onClick={() =>
                                                                                                handleSaveConfiguration(
                                                                                                    selectedUser,
                                                                                                    'reranker'
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Save
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            color="inherit"
                                                                                            startIcon={
                                                                                                <Close />
                                                                                            }
                                                                                            onClick={
                                                                                                handleCancelConfigurationEdit
                                                                                            }
                                                                                        >
                                                                                            Cancel
                                                                                        </Button>
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        startIcon={
                                                                                            <Edit />
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleEditConfiguration(
                                                                                                'reranker'
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </Button>
                                                                                )}
                                                                            </Box>
                                                                            {!hasApiKeyInConfiguration(
                                                                                selectedUser.user_reranker_configuration
                                                                            ) && (
                                                                                <Alert
                                                                                    severity="warning"
                                                                                    sx={{
                                                                                        mb: 1.5,
                                                                                    }}
                                                                                >
                                                                                    Missing
                                                                                    reranker
                                                                                    API
                                                                                    key.
                                                                                </Alert>
                                                                            )}
                                                                            {editingConfigurationType ===
                                                                            'reranker'
                                                                                ? renderModelConfigurationEditor(
                                                                                      'reranker'
                                                                                  )
                                                                                : renderModelConfigurationView(
                                                                                      selectedUser.user_reranker_configuration,
                                                                                      'reranker'
                                                                                  )}
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    size={{
                                                                        xs: 12,
                                                                        md: 4,
                                                                    }}
                                                                >
                                                                    <Card variant="outlined">
                                                                        <CardContent>
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'space-between',
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="subtitle1"
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: 1,
                                                                                    }}
                                                                                >
                                                                                    <Hub fontSize="small" />
                                                                                    Embedder
                                                                                </Typography>
                                                                                {editingConfigurationType ===
                                                                                'embedder' ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            display:
                                                                                                'flex',
                                                                                            gap: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="contained"
                                                                                            startIcon={
                                                                                                <Save />
                                                                                            }
                                                                                            onClick={() =>
                                                                                                handleSaveConfiguration(
                                                                                                    selectedUser,
                                                                                                    'embedder'
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Save
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="outlined"
                                                                                            color="inherit"
                                                                                            startIcon={
                                                                                                <Close />
                                                                                            }
                                                                                            onClick={
                                                                                                handleCancelConfigurationEdit
                                                                                            }
                                                                                        >
                                                                                            Cancel
                                                                                        </Button>
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        startIcon={
                                                                                            <Edit />
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleEditConfiguration(
                                                                                                'embedder'
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </Button>
                                                                                )}
                                                                            </Box>
                                                                            {!hasApiKeyInConfiguration(
                                                                                selectedUser.user_embedder_configuration
                                                                            ) && (
                                                                                <Alert
                                                                                    severity="warning"
                                                                                    sx={{
                                                                                        mb: 1.5,
                                                                                    }}
                                                                                >
                                                                                    Missing
                                                                                    embedding
                                                                                    API
                                                                                    key.
                                                                                </Alert>
                                                                            )}
                                                                            {editingConfigurationType ===
                                                                            'embedder'
                                                                                ? renderModelConfigurationEditor(
                                                                                      'embedder'
                                                                                  )
                                                                                : renderModelConfigurationView(
                                                                                      selectedUser.user_embedder_configuration,
                                                                                      'embedder'
                                                                                  )}
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <People
                                                sx={{
                                                    fontSize: 80,
                                                    color: 'grey.300',
                                                }}
                                            />
                                            <Typography
                                                variant="h6"
                                                color="text.secondary"
                                            >
                                                Select a user to view details
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                textAlign="center"
                                            >
                                                Choose a user from the list on
                                                the left to see full account
                                                information
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
